import React, { useState, useEffect, Fragment, useRef, useReducer, useCallback, useMemo, memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import axiosInstance from "../../../../api/axiosInstance";
import { useHeaderSlot } from "../../../../contexts/HeaderSlotContext";
import "primeicons/primeicons.css";
import {
  LuChevronRight as ChevronRight,
  LuMenu as Menu,
  LuBell as Bell,
  LuUser as User,
  LuLogOut as LogOut,
  LuChevronDown as ChevronDown,
} from "react-icons/lu";
import { getWsBaseUrl, getWsUrl } from "../../../../utils/wsUrl";
import { resolveAssetUrl } from "../../../../utils/resolveAssetUrl";

const ROUTE_MAP = {
  "/launchpad": "Launchpad",
  "/knowledge-base": "Knowledge Base",
  "/tickets": "Tickets",
  "/incidents": "Incidents",
  "/requests": "Requests",
  "/queues": "Queues",
  "/agents": "Agents",
  "/sla-policies": "SLA Policies",
  "/reports": "Reports",
  "/profile": "Profile",
  "/overview": "Dashboard",
  "/employee-support": "Employee Support",
  "/product-support": "Product Support",
  "/app-domain": "App / Department",
  "/agents-domains": "Agents & Departments",
  "/notifications": "Notifications",
  "/brand-hours": "Brand Hours",
  "/bulk-upload": "Bulk Upload",
  "/profile-setup": "Profile Setup",
  "/test": "Testing",
};

const MAIN_PAGE_PATHS = [
  "",
  "/",
  "/launchpad",
  "/overview",
  "/tickets",
  "/incidents",
  "/requests",
  "/agents",
  "/queues",
  "/knowledge-base",
  "/sla-policies",
  "/reports",
  "/app-domain",
  "/employee-support",
  "/product-support",
  "/notifications",
  "/profile",
  "/profile-setup",
  "/employees",
  "/form-render",
  "/brand-hours",
  "/bulk-upload",
  "/launchpad/create-knowledge-base",
  "/launchpad/create-assign-queue",
  "/test",
];

const toText = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    return value.content ?? value.message ?? value.name ?? value.title ?? value.token ?? JSON.stringify(value);
  }
  return "";
};

const safeStorageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const getTicketTitle = (ticketId, location) => {
  const fromState = location.state?.title;
  if (fromState) return toText(fromState);
  const stored = safeStorageGet(`ticket_title_${ticketId}`);
  if (stored) return toText(stored);
  return ticketId;
};

const NotificationBell = memo(() => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [projectadminId, setProjectadminId] = useState(null);
  const dropdownRef = useRef(null);
  const wsRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    const fetchId = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/v1/users/me`);
        if (isMounted) {
          setProjectadminId(data.body?.actual_id);
        }
      } catch (err) {
        console.error("Error fetching project admin ID:", err);
      }
    };
    fetchId();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!projectadminId) return;

    let isMounted = true;
    const fetchNotifications = async () => {
      try {
        const res = await axiosInstance.get(`/api/v1/notifications/by-user/${projectadminId}`);
        if (isMounted) {
          setNotifications(res.data.body || []);
        }
      } catch (err) {
        console.error("Error fetching notifications:", err);
      }
    };
    fetchNotifications();

    const ws = new WebSocket(getWsUrl(projectadminId));
    wsRef.current = ws;
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "NOTIFICATION") {
          setNotifications((prev) => [
            {
              id: data.id,
              title: data.title,
              message: data.message,
              link: data.link,
              created_at: data.created_at,
              is_read: false,
            },
            ...prev,
          ]);
        }
      } catch (err) {
        console.error("WS message error:", err);
      }
    };
    ws.onclose = () => console.log("Notification WebSocket closed");
    ws.onerror = (err) => console.error("WebSocket error:", err);

    return () => {
      isMounted = false;
      if (wsRef.current) wsRef.current.close();
    };
  }, [projectadminId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications]
  );

  const markAsRead = useCallback(async (notificationId) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
    );
    try {
      await axiosInstance.patch(`/api/v1/requests/notifications/${notificationId}/read-count`);
    } catch (err) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: false } : n))
      );
      console.error("Failed to mark as read", err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const unread = notifications.filter((n) => !n.is_read);
    if (unread.length === 0) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    await Promise.all(
      unread.map((n) =>
        axiosInstance.patch(`/api/v1/requests/notifications/${n.id}/read-count`).catch(() => { })
      )
    );
  }, [notifications]);

  const handleNotificationClick = useCallback(
    async (notification) => {
      if (!notification.is_read) await markAsRead(notification.id);

      let rawTarget = notification.link || notification.ticket_id || notification.target_id || "";

      if (!rawTarget) {
        const text = `${notification.title || ""} ${notification.message || ""}`;
        const match = text.match(/ST-[A-Za-z0-9]+/i);
        if (match) {
          rawTarget = match[0];
        }
      }

      if (rawTarget) {
        let path = String(rawTarget).trim();

        if (path.startsWith("http://") || path.startsWith("https://")) {
          try {
            path = new URL(path).pathname;
          } catch (e) { }
        }

        if (path.startsWith("/admin/project/tickets/")) {
          navigate(path);
        } else if (path.startsWith("/tickets/")) {
          const id = path.replace("/tickets/", "");
          navigate(`/admin/project/tickets/${id}`);
        } else if (/^[A-Za-z0-9_-]+$/.test(path)) {
          navigate(`/admin/project/tickets/${path}`);
        } else {
          if (!path.startsWith("/admin/project")) {
            path = `/admin/project${path.startsWith("/") ? "" : "/"}${path}`;
          }
          navigate(path);
        }
      } else {
        navigate("/admin/project/tickets");
      }

      setOpen(false);
    },
    [markAsRead, navigate]
  );

  const getRelativeTime = useCallback((ts) => {
    if (!ts) return "";
    try {
      return formatDistanceToNow(new Date(ts), { addSuffix: true });
    } catch {
      return "";
    }
  }, []);

  const toggleDropdown = useCallback(() => setOpen((v) => !v), []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors hover:bg-gray-100"
      >
        <Bell className="w-6 h-6 text-[#3B3D41]" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-[#3250FF] text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[calc(100vw-24px)] sm:w-[350px] bg-white rounded-[20px] p-3 border border-gray-100 shadow-[0_10px_35px_rgba(0,0,0,0.12)] z-50 overflow-hidden">
          <div className="flex items-center justify-between px-1 pb-3">
            <h3 className="text-[17px] font-bold text-[#1E293B]">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs font-semibold text-[#603CFF] hover:text-[#4F33E0] transition-colors"
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="space-y-2 max-h-[360px] overflow-y-auto pr-0.5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <Bell className="w-4 h-4 text-gray-400" />
                </div>
                <p className="text-xs text-gray-400">No notifications yet</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`p-2 rounded-xl cursor-pointer transition-all ${!n.is_read
                      ? "bg-[#E7EBFF] hover:bg-[#E0E0FB]"
                      : "bg-white border border-gray-100 hover:bg-gray-50"
                    }`}
                >
                  <p className={`text-[13px] leading-snug font-medium ${!n.is_read ? "text-[#1E293B]" : "text-[#475569]"}`}>
                    {toText(n.message || n.title)}
                  </p>
                  <p className="text-[11px] text-[#94A3B8] mt-1">
                    {getRelativeTime(n.created_at)}
                  </p>
                </div>
              ))
            )}
          </div>

          {notifications.length > 10 && (
            <div
              className="mt-3 pt-2 text-center text-xs font-semibold cursor-pointer text-[#603CFF] hover:text-[#4F33E0] transition-colors border-t border-gray-100"
              onClick={() => {
                navigate("/admin/project/notifications");
                setOpen(false);
              }}
            >
              View all {notifications.length} notifications →
            </div>
          )}
        </div>
      )}
    </div>
  );
});

NotificationBell.displayName = "NotificationBell";

const UserDropdown = memo(({ isOpen, onToggle, onLogout, navigate }) => {
  const dropdownRef = useRef(null);
  const [userName, setUserName] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.actual_name || u.name || u.full_name || "Project Admin";
    } catch {
      return "Project Admin";
    }
  });
  const [userEmail, setUserEmail] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.email || "";
    } catch {
      return "";
    }
  });

  const [avatarUrl, setAvatarUrl] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.avatar_url || u.avatar || u.profile_image || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    let isMounted = true;
    const fetchUser = async () => {
      try {
        const { data } = await axiosInstance.get("/api/v1/users/me");
        if (isMounted) {
          const body = data.body || data || {};
          if (body.actual_name || body.name || body.full_name) {
            setUserName(body.actual_name || body.name || body.full_name);
          }
          if (body.email) {
            setUserEmail(body.email);
          }
          const av = body.avatar_url || body.avatar || body.profile_pic || body.profile_image;
          if (av) {
            setAvatarUrl(av);
          }
        }
      } catch (err) {
        console.error("Error fetching user details in header:", err);
      }
    };
    fetchUser();
    return () => {
      isMounted = false;
    };
  }, []);

  const name = toText(userName);
  const email = toText(userEmail);
  const initials = useMemo(
    () =>
      name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join("") || "PA",
    [name]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onToggle(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onToggle]);

  const handleProfileClick = useCallback(() => {
    navigate("/admin/project/profile");
    onToggle(false);
  }, [navigate, onToggle]);

  const handleToggleClick = useCallback(() => {
    onToggle(!isOpen);
  }, [isOpen, onToggle]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggleClick}
        className="flex items-center px-2 py-1 gap-4 justify-between rounded-full bg-[#FFFFFFB2] border border-[#EBE7F8] hover:bg-white transition-colors"
      >
        <div className="flex items-center w-9 h-9 bg-[#1B1730] rounded-full justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
          {avatarUrl ? (
            <img
              src={resolveAssetUrl(avatarUrl)}
              alt={name}
              className="w-full h-full object-cover"
            />
          ) : (
            initials || <User className="w-4 h-4" />
          )}
        </div>
        <div className="hidden sm:flex flex-col items-start leading-[1.1] min-w-0 flex-1">
          <span className="text-sm font-semibold text-[#1B1730] truncate w-full text-left">{name}</span>
          <span className="text-xs text-gray-500 truncate w-full text-left mt-0.5">Project Admin</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-800 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl border border-gray-200 shadow-lg z-50 overflow-hidden">
          <div
            className="px-4 py-3 flex items-center gap-3"
            style={{ background: "#FFFFFF", borderBottom: "1px solid #DDE3EA" }}
          >
            <div className="w-8 h-8 rounded-full bg-[#1B1730] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
              {avatarUrl ? (
                <img
                  src={resolveAssetUrl(avatarUrl)}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              ) : (
                initials
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{name}</p>
              {email && <p className="text-xs text-slate-700 truncate mt-0.5">{email}</p>}
            </div>
          </div>
          <div className="py-1">
            <button
              onClick={handleProfileClick}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-800 hover:bg-[#F3F5F7] transition-colors"
            >
              <User className="w-4 h-4 text-slate-600" />
              My Profile
            </button>
            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

UserDropdown.displayName = "UserDropdown";

const Breadcrumb = memo(({ navigate }) => {
  const location = useLocation();
  const rawPath = location.pathname;
  const basePrefix = "/admin/project";
  const relPath = rawPath.startsWith(basePrefix) ? rawPath.slice(basePrefix.length) : rawPath;

  const [, forceRender] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    const onUpdate = () => forceRender();
    window.addEventListener("applicationNameUpdated", onUpdate);
    window.addEventListener("domainNameUpdated", onUpdate);
    window.addEventListener("queueNameUpdated", onUpdate);
    window.addEventListener("agentNameUpdated", onUpdate);
    window.addEventListener("kbArticleNameUpdated", onUpdate);
    window.addEventListener("employeeNameUpdated", onUpdate);
    return () => {
      window.removeEventListener("applicationNameUpdated", onUpdate);
      window.removeEventListener("domainNameUpdated", onUpdate);
      window.removeEventListener("queueNameUpdated", onUpdate);
      window.removeEventListener("agentNameUpdated", onUpdate);
      window.removeEventListener("kbArticleNameUpdated", onUpdate);
      window.removeEventListener("employeeNameUpdated", onUpdate);
    };
  }, []);

  const queueNameFromState = useMemo(
    () =>
      toText(location.state?.queueName) ||
      toText(safeStorageGet(`queue_name_${relPath.split("/").pop()}`)) ||
      null,
    [location.state, relPath]
  );

  const agentNameFromState = useCallback(
    (agentId) =>
      toText(location.state?.agentName) ||
      toText(safeStorageGet(`agent_name_${agentId}`)) ||
      null,
    [location.state]
  );

  const kbArticleNameFromState = useCallback(
    (articleId) =>
      toText(location.state?.articleTitle) ||
      toText(safeStorageGet(`kb_article_name_${articleId}`)) ||
      null,
    [location.state]
  );

  const appNameFromState = useCallback(
    (appId) =>
      toText(location.state?.appName) ||
      toText(safeStorageGet(`application_name_${appId}`)) ||
      null,
    [location.state]
  );

  const domainNameFromState = useCallback(
    (domainId) =>
      toText(location.state?.domainName) ||
      toText(safeStorageGet(`domain_name_${domainId}`)) ||
      null,
    [location.state]
  );

  const formNameFromState = useCallback(
    (formId) =>
      toText(location.state?.formName) ||
      toText(safeStorageGet(`form_name_${formId}`)) ||
      null,
    [location.state]
  );

  const employeeNameFromState = useCallback(
    (employeeId) =>
      toText(location.state?.employee?.name) ||
      toText(location.state?.employeeName) ||
      toText(safeStorageGet(`employee_name_${employeeId}`)) ||
      null,
    [location.state]
  );

  const breadcrumbs = useMemo(() => {
    const list = [];
    const addCrumb = (name, path = null) => list.push({ name: toText(name), path });

    if (relPath === "" || relPath === "/" || relPath === "/launchpad") {
    } else if (relPath === "/launchpad/create-knowledge-base") {
      addCrumb("Add Article page");
    } else if (relPath === "/launchpad/create-assign-queue") {
      addCrumb("Add Queue");
    } else if (relPath === "/brand-hours") {
      addCrumb("Bussiness Hours");
    } else if (relPath === "/bulk-upload") {
      addCrumb("Bulk Import");
    } else if (relPath === "/bulk-upload/preview") {
      addCrumb("Launchpad", `${basePrefix}/launchpad`);
      addCrumb("Bulk Upload");
      addCrumb("Preview");
    } else if (relPath === "/tickets") {
      addCrumb("Tickets Overview");
    } else if (relPath.startsWith("/tickets/")) {
      const ticketId = relPath.split("/").pop();
      addCrumb("Tickets Overview", `${basePrefix}/tickets`);
      const title = getTicketTitle(ticketId, location);
      addCrumb(title);
    } else if (relPath === "/employee-support") {
      addCrumb("Employee Support");
    } else if (relPath === "/product-support") {
      addCrumb("Product Support");
    } else if (relPath === "/app-domain") {
      addCrumb("App / Department Overview");
    } else if (relPath === "/profile") {
      addCrumb("Profile");
    } else if (relPath === "/profile-setup") {
      addCrumb("Profile Setup");
    } else if (relPath === "/agents") {
      addCrumb("Agent Overview");
    } else if (relPath === "/agents-domains") {
      addCrumb("Agent Overview", `${basePrefix}/agents`);
      addCrumb("Add Agent");
    } else if (relPath.startsWith("/agents/edit-agent/")) {
      const agentId = relPath.split("/").pop();
      const agentName = agentNameFromState(agentId);
      addCrumb("Agent Overview", `${basePrefix}/agents`);
      addCrumb(agentName ? `Edit ${agentName}` : "Edit Agent", `${basePrefix}/agents/${agentId}`);
    } else if (relPath.startsWith("/agents/")) {
      const agentId = relPath.split("/").pop();
      if (agentId !== "edit-agent") {
        addCrumb("Agent Overview", `${basePrefix}/agents`);
        addCrumb(agentNameFromState(agentId) || agentId);
      }
    } else if (relPath === "/queues") {
      addCrumb("Queue Overview");
    } else if (relPath === "/queues/create-assign-queue") {
      addCrumb("Queue Overview", `${basePrefix}/queues`);
      addCrumb("Add Queue");
    } else if (relPath.startsWith("/queues/edit-assign-queue/")) {
      const queueId = relPath.split("/").pop();
      const queueName = toText(location.state?.queueName) || toText(safeStorageGet(`queue_name_${queueId}`));
      addCrumb("Queue Overview", `${basePrefix}/queues`);
      addCrumb(queueName ? `Edit ${queueName}` : "Edit Queue");
    } else if (relPath.startsWith("/queues/view/")) {
      const queueId = relPath.split("/").pop();
      addCrumb("Queue Overview", `${basePrefix}/queues`);
      addCrumb(queueNameFromState || safeStorageGet(`queue_name_${queueId}`) || queueId);
    } else if (relPath === "/notifications") {
      addCrumb("Notifications");
    } else if (relPath === "/knowledge-base") {
      addCrumb("Knowledge Base Overview");
    } else if (relPath === "/knowledge-base/setup") {
      addCrumb("Knowledge Base Overview", `${basePrefix}/knowledge-base`);
      addCrumb("Add Category");
    } else if (relPath === "/knowledge-base/create-knowledge-base") {
      addCrumb("Knowledge Base Overview", `${basePrefix}/knowledge-base`);
      addCrumb("Add Article");
    } else if (relPath === "/knowledge-base/bulk-upload-knowledge-base") {
      addCrumb("Knowledge Base Overview", `${basePrefix}/knowledge-base`);
      addCrumb("Bulk Upload");
    } else if (relPath.startsWith("/knowledge-base/edit-knowledge-base/")) {
      const articleId = relPath.split("/").pop();
      const articleName = kbArticleNameFromState(articleId);
      addCrumb("Knowledge Base Overview", `${basePrefix}/knowledge-base`);
      addCrumb(articleName ? `Edit ${articleName}` : "Edit Article");
    } else if (relPath.startsWith("/knowledge-base/article/")) {
      const articleId = relPath.split("/").pop();
      addCrumb("Knowledge Base Overview", `${basePrefix}/knowledge-base`);
      addCrumb(kbArticleNameFromState(articleId) || "Article Preview");
    } else if (relPath === "/reports") {
      addCrumb("Reports Overview");
    } else if (relPath.startsWith("/applications/")) {
      const appId = relPath.split("/").pop();
      addCrumb("App / Department Overview", `${basePrefix}/app-domain`);
      addCrumb(appNameFromState(appId) || "Application");
    } else if (relPath.startsWith("/custom-form/")) {
      const id = relPath.split("/").pop();
      const parentAppId = location.state?.appId || safeStorageGet(`form_appId_${id}`);
      const parentAppName =
        toText(location.state?.appName) ||
        (parentAppId && toText(safeStorageGet(`application_name_${parentAppId}`))) ||
        toText(safeStorageGet(`form_appName_${id}`));
      addCrumb("App / Department Overview", `${basePrefix}/app-domain`);
      if (parentAppId) addCrumb(parentAppName || parentAppId, `${basePrefix}/applications/${parentAppId}`);
      addCrumb(formNameFromState(id) || id);
    } else if (relPath.startsWith("/add-request/")) {
      const appId = relPath.split("/").pop();
      addCrumb("App / Department Overview", `${basePrefix}/app-domain`);
      addCrumb(appNameFromState(appId) || appId, `${basePrefix}/applications/${appId}`);
      addCrumb("Add Request");
    } else if (relPath === "/form-render") {
      addCrumb("Form Renderer");
    } else if (relPath.startsWith("/domains/")) {
      const domainId = relPath.split("/").pop();
      addCrumb("App / Department Overview", `${basePrefix}/app-domain`);
      addCrumb(toText(location.state?.domainName) || toText(safeStorageGet(`domain_name_${domainId}`)) || "Department");
    } else if (relPath.startsWith("/domain-form/edit/")) {
      const formId = relPath.split("/").pop();
      const parentDomainId = location.state?.domainId || safeStorageGet(`form_domainId_${formId}`);
      const parentDomainName =
        toText(location.state?.domainName) ||
        (parentDomainId && toText(safeStorageGet(`domain_name_${parentDomainId}`))) ||
        toText(safeStorageGet(`form_domainName_${formId}`));
      addCrumb("App / Department Overview", `${basePrefix}/app-domain`);
      if (parentDomainId) addCrumb(parentDomainName || parentDomainId, `${basePrefix}/domains/${parentDomainId}`);
      addCrumb(formNameFromState(formId) || formId);
    } else if (relPath.startsWith("/domain-form/")) {
      const domainId = relPath.split("/").pop();
      const parentDomainName = toText(location.state?.domainName) || toText(safeStorageGet(`domain_name_${domainId}`));
      addCrumb("App / Department Overview", `${basePrefix}/app-domain`);
      if (domainId) addCrumb(parentDomainName || domainId, `${basePrefix}/domains/${domainId}`);
      addCrumb("New Form");
    } else if (relPath === "/test") {
      addCrumb("Testing");
    } else if (relPath === "/employees") {
      addCrumb("Employees");
    } else if (relPath === "/employees/onboard") {
      addCrumb("Employees", `${basePrefix}/employees`);
      addCrumb("Onboard Employee");
    } else if (relPath.startsWith("/employees/")) {
      const employeeId = relPath.split("/").pop();
      addCrumb("Employees", `${basePrefix}/employees`);
      addCrumb(employeeNameFromState(employeeId) || "Employee");
    } else {
      const segments = relPath.split("/").filter(Boolean);
      let cumulative = "";
      segments.forEach((segment) => {
        cumulative += `/${segment}`;
        const mapped = ROUTE_MAP[cumulative] || segment.replace(/-/g, " ");
        const label = mapped.charAt(0).toUpperCase() + mapped.slice(1);
        addCrumb(label, `${basePrefix}${cumulative}`);
      });
    }
    return list;
  }, [
    relPath,
    location,
    queueNameFromState,
    agentNameFromState,
    kbArticleNameFromState,
    appNameFromState,
    domainNameFromState,
    formNameFromState,
    employeeNameFromState,
  ]);

  const handleHomeClick = useCallback(() => {
    navigate(`${basePrefix}/overview`);
  }, [navigate]);

  return (
    <div className="flex items-center space-x-1 text-[13px] text-gray-600 flex-wrap">
      <i
        className="pi pi-home text-[#475569] cursor-pointer hover:text-[#3250FF] transition-colors"
        onClick={handleHomeClick}
        title="Home"
      />
      {breadcrumbs.length > 0 && <ChevronRight className="h-6 w-6 text-[#3250FF] flex-shrink-0" />}
      {breadcrumbs.map((crumb, index) => (
        <Fragment key={index}>
          {index > 0 && <ChevronRight className="h-5 w-5 text-[#3250FF] flex-shrink-0" />}
          <span
            onClick={() => index < breadcrumbs.length - 1 && crumb.path && navigate(crumb.path)}
            className={`${index < breadcrumbs.length - 1 && crumb.path
                ? "text-[#3250FF] hover:text-[#2036CC] hover:underline cursor-pointer font-medium"
                : "text-[#0F172A] font-semibold"
              } truncate max-w-[200px] transition-colors`}
          >
            {crumb.name}
          </span>
        </Fragment>
      ))}
    </div>
  );
});

Breadcrumb.displayName = "Breadcrumb";

const ProjectAdminHeader = ({ onOpenSidenav }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const { slot, titleSlot } = useHeaderSlot();
  const [, forceRender] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    const onUpdate = () => forceRender();
    window.addEventListener("applicationNameUpdated", onUpdate);
    window.addEventListener("domainNameUpdated", onUpdate);
    window.addEventListener("queueNameUpdated", onUpdate);
    window.addEventListener("agentNameUpdated", onUpdate);
    window.addEventListener("kbArticleNameUpdated", onUpdate);
    window.addEventListener("employeeNameUpdated", onUpdate);
    return () => {
      window.removeEventListener("applicationNameUpdated", onUpdate);
      window.removeEventListener("domainNameUpdated", onUpdate);
      window.removeEventListener("queueNameUpdated", onUpdate);
      window.removeEventListener("agentNameUpdated", onUpdate);
      window.removeEventListener("kbArticleNameUpdated", onUpdate);
      window.removeEventListener("employeeNameUpdated", onUpdate);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const path = useMemo(
    () => location.pathname.replace("/admin/project", ""),
    [location.pathname]
  );

  const routeKey = useMemo(
    () => Object.keys(ROUTE_MAP).find((key) => location.pathname.includes(key)),
    [location.pathname]
  );

  const isOverviewPage = useMemo(
    () => path === "/overview" || path === "" || path === "/" || path === "/launchpad",
    [path]
  );

  const isMainPage = useMemo(
    () => MAIN_PAGE_PATHS.includes(path),
    [path]
  );

  const resolveAgentName = useCallback(
    (agentId) => toText(location.state?.agentName) || toText(safeStorageGet(`agent_name_${agentId}`)) || null,
    [location.state]
  );

  const resolveKbArticleName = useCallback(
    (articleId) => toText(location.state?.articleTitle) || toText(safeStorageGet(`kb_article_name_${articleId}`)) || null,
    [location.state]
  );

  const resolveAppName = useCallback(
    (appId) => toText(location.state?.appName) || toText(safeStorageGet(`application_name_${appId}`)) || null,
    [location.state]
  );

  const resolveDomainName = useCallback(
    (domainId) => toText(location.state?.domainName) || toText(safeStorageGet(`domain_name_${domainId}`)) || null,
    [location.state]
  );

  const resolveFormName = useCallback(
    (formId) => toText(location.state?.formName) || toText(safeStorageGet(`form_name_${formId}`)) || null,
    [location.state]
  );

  const resolveEmployeeName = useCallback(
    (employeeId) =>
      toText(location.state?.employee?.name) ||
      toText(location.state?.employeeName) ||
      toText(safeStorageGet(`employee_name_${employeeId}`)) ||
      null,
    [location.state]
  );

  const currentHeading = useMemo(() => {
    if (path === "" || path === "/" || path === "/launchpad") return "Launchpad";
    if (path === "/launchpad/create-knowledge-base") return "Add Article";
    if (path === "/launchpad/create-assign-queue") return "Add Queue";

    if (path === "/employees") return "Employees";
    if (path === "/employees/onboard") return "Onboard Employee";
    if (path.startsWith("/employees/")) {
      const employeeId = path.split("/").pop();
      return resolveEmployeeName(employeeId) || "Employee Details";
    }

    if (path.startsWith("/agents/edit-agent/")) {
      const agentId = path.split("/").pop();
      return resolveAgentName(agentId) || "Edit Agent";
    }
    if (path.startsWith("/agents/")) {
      const agentId = path.split("/").pop();
      if (agentId !== "edit-agent") return resolveAgentName(agentId) || agentId;
    }

    if (path.startsWith("/queues/view/")) {
      const queueId = path.split("/").pop();
      const storedQueueName = safeStorageGet(`queue_name_${queueId}`);
      return toText(location.state?.queueName) || (storedQueueName && storedQueueName !== queueId ? storedQueueName : "Queue Details");
    }
    if (path.startsWith("/queues/edit-assign-queue/")) {
      const queueId = path.split("/").pop();
      const storedQueueName = safeStorageGet(`queue_name_${queueId}`);
      const queueName = toText(location.state?.queueName) || (storedQueueName && storedQueueName !== queueId ? storedQueueName : null);
      return queueName || "Edit Queue";
    }
    if (path === "/queues/create-assign-queue") return "Add Queue";
    if (path === "/queues") return "Queue";

    if (path === "/knowledge-base/setup") return "Add Category";
    if (path === "/knowledge-base/create-knowledge-base") return "Add Article";
    if (path === "/knowledge-base/bulk-upload-knowledge-base") return "Bulk Upload KB";
    if (path.startsWith("/knowledge-base/edit-knowledge-base/")) {
      const articleId = path.split("/").pop();
      return resolveKbArticleName(articleId) || "Edit Article";
    }
    if (path.startsWith("/knowledge-base/article/")) {
      const articleId = path.split("/").pop();
      return resolveKbArticleName(articleId) || "Article Preview";
    }

    if (path.startsWith("/applications/")) {
      const appId = path.split("/").pop();
      return resolveAppName(appId) || `Application ${appId}`;
    }
    if (path.startsWith("/custom-form/")) {
      const id = path.split("/").pop();
      return resolveFormName(id) || "Custom Form";
    }
    if (path.startsWith("/add-request/")) {
      const id = path.split("/").pop();
      const appName = resolveAppName(id);
      if (appName && appName !== id) return appName;
      return resolveDomainName(id) || id;
    }

    if (path.startsWith("/domains/")) {
      const domainId = path.split("/").pop();
      return resolveDomainName(domainId) || `Department ${domainId}`;
    }
    if (path.startsWith("/domain-form/")) {
      const domainId = path.split("/").pop();
      return resolveDomainName(domainId) || `Department ${domainId}`;
    }
    if (path.startsWith("/domain-form/edit/")) {
      const formId = path.split("/").pop();
      return resolveFormName(formId) || "Edit Form";
    }

    if (path.startsWith("/tickets/")) {
      const ticketId = path.split("/").pop();
      return getTicketTitle(ticketId, location);
    }

    if (path === "/domain-form/") return "Department Form";
    if (path === "/form-render") return "Form Renderer";
    if (path === "/app-domain") return "Settings";
    if (path === "/profile-setup") return "Profile Setup";
    if (path === "/test") return "Testing";

    const mapped =
      ROUTE_MAP[routeKey] ||
      path.split("/").filter(Boolean).pop()?.replace(/-/g, " ") ||
      "Launchpad";

    return mapped.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  }, [
    path,
    location,
    routeKey,
    resolveAgentName,
    resolveKbArticleName,
    resolveAppName,
    resolveDomainName,
    resolveFormName,
    resolveEmployeeName,
  ]);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    // DEF-178: chat/session state lives in sessionStorage, which
    // localStorage.clear() leaves untouched — clear it so the next user in
    // this tab can't resume the previous one's session.
    try { sessionStorage.clear(); } catch { /* unavailable — non-fatal */ }
    navigate("/auth/sign-in");
  }, [navigate]);

  const handleHomeClick = useCallback(() => {
    navigate("/admin/project/overview");
  }, [navigate]);

  return (
    <div className="w-full">
      <div
        className="flex pt-5 pb-2 px-6 items-center justify-between w-full"
        style={{
          background: 'transparent',
          opacity: 1,
        }}
      >
        <div className="flex items-center min-w-0 flex-1">
          {isMobile && (
            <button
              onClick={onOpenSidenav}
              className="mr-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
          )}
          <div className="flex flex-col justify-center min-w-0">
            <div className="flex items-center gap-2">
              {!isMobile && isOverviewPage && (
                <i
                  className="pi pi-home text-gray-400 text-lg cursor-pointer transition-colors hover:text-[#3250FF]"
                  onClick={handleHomeClick}
                  title="Home"
                />
              )}
              {titleSlot ? (
                <div className="flex items-center">{titleSlot}</div>
              ) : (
                <h1 className="text-xl font-semibold text-[#0F172A] truncate capitalize max-w-[180px] sm:max-w-none">
                  {toText(currentHeading)}
                </h1>
              )}
            </div>
            {!isOverviewPage && (
              <div className="mt-0.5 hidden sm:flex">
                <Breadcrumb navigate={navigate} />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3 ml-4">
          {slot && <div className="flex items-center">{slot}</div>}
          <div className="flex items-center space-x-2">
            <NotificationBell />
            <UserDropdown
              isOpen={showDropdown}
              onToggle={setShowDropdown}
              onLogout={handleLogout}
              navigate={navigate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ProjectAdminHeader);
